/**
 * Conditional modal rendering for the single-order header.
 *
 * Displays form-selection, happy-flow, comment-RPA, NAR-RPA-trigger,
 * and clinical-data modals based on the currently opened modal ID.
 * @module headerModals
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import ClinicalDataTable from "../../../../../components/modals/clinicalDataTable/ClinicalDataTable";
import CommentRpaNewModal from "../../../../../components/modals/commentRPAnewModal/commentRpaNewModal";
import FormSelectionModal from "../../../../../components/modals/formSelectionModal/formSelectionModal";
import HappyFlowModal from "../../../../../components/modals/happyFlowModal/happyFlowModal";
import NarRpaTriggerModal from "../../../../../components/modals/narRPATrigger/narRpaTriggerModal";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { ModalId } from "../../../../../enums/modalId";
import { shouldShowReassignmentCheckbox } from "../../../../../utils/organizationHelper";

/** Props for {@link HeaderModals}. */
interface HeaderModalsProps {
  /** Currently active modal identifier from Redux. */
  openedModalId: string | null;
  /** PA order ID passed to child modals. */
  orderId: string;
  /** General order details for status and context. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Auth-on-file details for auth-status checks. */
  authOnFileDetails?: AuthOnFile;
  /** Manual auth status from BO context. */
  manualAuthStatus?: string;
  /** Callback to close the active modal. */
  handleCloseModal: () => void;
  /** Callback invoked when the happy-flow modal is saved. */
  handleHappyFlowModalSave: (selectedOrderIds?: string[]) => void;
  /** Callback invoked when the comment-RPA modal is saved. */
  handleCommentRpaModalSave: () => void;
  /** Whether a save operation is in progress. */
  isLoading: boolean;
  /** Mutable ref tracking the selected provider for reassignment. */
  selectedProvider: React.MutableRefObject<{ id: string; name: string }>;
}

/**
 * Renders header-level modals (form selection, happy-flow, comment-RPA,
 * NAR-RPA trigger, clinical data) based on the currently opened modal ID.
 *
 * @param props - Modal state, order context, and save/close handlers
 * @returns The active modal JSX or an empty fragment
 */
const HeaderModals = ({
  openedModalId,
  orderId,
  // singleMedicalPaOrder,
  generalOrderDetails,
  authOnFileDetails,
  manualAuthStatus,
  handleCloseModal,
  handleHappyFlowModalSave,
  handleCommentRpaModalSave,
  isLoading,
  selectedProvider,
}: HeaderModalsProps) => {
  return (
    <>
      {openedModalId === ModalId.FORM_SELECTION_MODAL && (
        <FormSelectionModal
          generalOrderDetails={
            generalOrderDetails ?? ({} as GeneralOrderDetails)
          }
        />
      )}
      {openedModalId === ModalId.HAPPY_FLOW_MODAL && (
        <HappyFlowModal
          onClose={handleCloseModal}
          onSave={handleHappyFlowModalSave}
          isLoading={isLoading}
          showReassignmentCheckbox={shouldShowReassignmentCheckbox(
            authOnFileDetails?.auth_status,
          )}
          onSelectedProviderChange={(provider) => {
            selectedProvider.current = provider;
          }}
          authStatus={authOnFileDetails?.auth_status ?? ""}
        />
      )}
      {openedModalId === ModalId.COMMENT_RPA_MODAL_NEW && (
        <CommentRpaNewModal
          onClose={handleCloseModal}
          onSave={handleCommentRpaModalSave}
          wrtiteBackStatus={
            generalOrderDetails?.status?.ev_write_back_status ?? ""
          }
          docRpaStatus={
            generalOrderDetails?.status?.document_upload_status ?? ""
          }
          id={generalOrderDetails?.order_id ?? ""}
          orderId={orderId}
          initialAuthStatus={authOnFileDetails?.auth_status ?? ""}
          auth_status={manualAuthStatus ?? undefined}
          generalOrderDetails={generalOrderDetails}
          authOnFileDetails={authOnFileDetails}
        />
      )}
      {openedModalId === ModalId.NAR_RPA_TRIGGER_MODAL && (
        <NarRpaTriggerModal onClose={handleCloseModal} metaData={{ orderId }} />
      )}
      {openedModalId === ModalId.CLINICAL_DATA_TABLE && (
        <ClinicalDataTable
          screenName={ScreenNamesForBigQuery.AUTH_STATUS_SCREEN}
          data={{
            entries: [],
          }}
          onSave={() => {
            toast.success(ToastMessages.ICD_10_SAVED_SUCCESSFULLY);
            handleCloseModal();
          }}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default HeaderModals;
